import { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { DERBY } from '@bb/shared';
import type { ProgressNews } from '../net/api';
import { RaceScene, type RaceEvents, type RaceHud } from '../game/RaceScene';

type Result = { place: number | null; coins: number; xp: number; progress?: ProgressNews };
const ordinal = (n: number) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

export function Derby({ onDone }: { onDone: (news?: ProgressNews) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const input = useRef({ throttle: 0, steer: 0 });
  const [hud, setHud] = useState<RaceHud>();
  const [result, setResult] = useState<Result>();
  const [error, setError] = useState<string>();
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!host.current) return;
    const forceCanvas = new URLSearchParams(window.location.search).get('renderer') === 'canvas';
    let game: Phaser.Game | undefined;
    const timer = window.setTimeout(() => {
      game = new Phaser.Game({ type: forceCanvas ? Phaser.CANVAS : Phaser.AUTO, parent: host.current!, width: DERBY.width, height: DERBY.height, scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH }, input: { windowEvents: false }, banner: false });
      const events: RaceEvents = {
        input: input.current,
        onHud: setHud,
        onResult: (r) => {
          setResult(r);
          onDone(r.progress);
        },
        onError: setError,
      };
      game.scene.add('race', RaceScene, true, events);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      game?.destroy(true);
    };
  }, [round]);

  const hold = (key: 'throttle' | 'steer', v: number) => ({
    onPointerDown: () => (input.current[key] = v),
    onPointerUp: () => (input.current[key] = 0),
    onPointerLeave: () => (input.current[key] = 0),
  });

  const status = !hud
    ? 'Finding a race…'
    : hud.phase === 'waiting'
      ? `Waiting for racers (${hud.racers}/${DERBY.maxRacers}). Starting soon…`
      : hud.phase === 'countdown'
        ? `Get ready… ${Math.max(1, Math.ceil(hud.countdown / 1000))}`
        : hud.phase === 'racing'
          ? hud.place
            ? `Finished ${ordinal(hud.place)}!`
            : `Lap ${Math.min(hud.lap + 1, DERBY.laps)} of ${DERBY.laps} · ${(hud.elapsedMs / 1000).toFixed(1)} s`
          : 'Race over';

  return (
    <div className="derby">
      <p className="prompt" role="status">{error ?? status}</p>
      <div className="derby-stage" ref={host} key={round} />
      <div className="derby-pad">
        <button className="btn ghost" {...hold('steer', -1)} aria-label="Steer left">◀</button>
        <button className="btn" {...hold('throttle', 1)} aria-label="Accelerate">Go</button>
        <button className="btn ghost" {...hold('throttle', -1)} aria-label="Brake">Brake</button>
        <button className="btn ghost" {...hold('steer', 1)} aria-label="Steer right">▶</button>
      </div>
      <p className="muted small-print">Arrow keys or WASD to drive. Three laps, clockwise. Staying on the sand is faster than the grass!</p>
      {result && (
        <div className="result">
          <strong className="big-score">{result.place ? `${ordinal(result.place)} place` : "Didn't finish"}</strong>
          <p>+{result.coins} coins · +{result.xp} XP</p>
          <button className="btn" onClick={() => { setResult(undefined); setHud(undefined); setRound((n) => n + 1); }}>Race again</button>
        </div>
      )}
    </div>
  );
}
